import { Logo } from "./Logo.js";
import { MapPin, Phone, Mail, Clock, ShieldCheck, ExternalLink, Building2 } from "lucide-react";

export const Footer = ({ settings, lang, onNavigate }) => {
  return (
    <footer className="bg-emerald-950 text-emerald-200/90 pt-16 pb-12 border-t border-emerald-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 pb-12 border-b border-emerald-900/60">
          {/* Col 1: Brand & Identity */}
          <div className="space-y-4">
            <Logo
              theme="dark"
              size="md"
              logoUrl={settings?.logo_url}
              siteName={lang === "bn" ? settings?.site_name_bn : settings?.site_name}
            />
            <p className="text-xs text-emerald-200/70 leading-relaxed font-sans">
              {lang === "bn"
                ? settings?.footer_about_text_bn || "পবিত্র কুরআনের নুরানি আলো এবং আধুনিক যুগের বিজ্ঞান, গণিত ও ভাষার সুষম সমন্বয়ে গঠিত দাগনভূঞার শ্রেষ্ঠ আদর্শ দ্বীনি শিক্ষাপ্রতিষ্ঠান।"
                : settings?.footer_about_text || "A premier model Islamic educational institution merging authentic Quranic memorization, character building, and standard NCTB academic curriculum."}
            </p>
            <div className="text-xs text-amber-300 font-arabic pt-1">
              {settings?.bismillah_header}
            </div>
            <div className="text-xs text-emerald-400 font-medium">
              ESTD. {settings?.established_year} · {lang === "bn" ? settings?.address_bn || "দাগনভূঞা, ফেনী" : settings?.address || "Daganbhuiyan, Feni"}
            </div>
          </div>

          {/* Col 2: Integrated Disciplines */}
          <div>
            <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-4 border-b border-emerald-800 pb-2">
              {lang === "bn" ? "সমন্বিত কারিকুলাম" : "Integrated Curriculum"}
            </h4>
            <ul className="space-y-2.5 text-xs text-emerald-200/80">
              <li className="flex items-center gap-2">
                <span className="text-amber-400 font-bold">১.</span>
                <span>{lang === "bn" ? "বাংলা (সাহিত্য, ব্যাকরণ ও সুন্দর হস্তলিপি)" : "Bangla (Literature, Grammar & Penmanship)"}</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-amber-400 font-bold">২.</span>
                <span>{lang === "bn" ? "আরবি (নূরানী কায়দা, তাজবীদ ও কথ্য ভাষা)" : "Arabic (Noorani Tajweed & Spoken Language)"}</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-amber-400 font-bold">৩.</span>
                <span>{lang === "bn" ? "ফার্সি (নৈতিক কবিতা ও ক্লাসিক্যাল সাহিত্য)" : "Farsi (Classical Moral Verses & Pandnama)"}</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-amber-400 font-bold">৪.</span>
                <span>{lang === "bn" ? "ইংরেজি (টানা হাতের লেখা ও ব্যাকরণ)" : "English (Cursive Script, Phonics & Grammar)"}</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-amber-400 font-bold">৫.</span>
                <span>{lang === "bn" ? "গণিত ও প্রাথমিক বিজ্ঞান" : "Mathematics & General Science"}</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-amber-400 font-bold">৬.</span>
                <span>{lang === "bn" ? "তাহফিজুল কুরআন (হিফজুল কুরআন বিভাগ)" : "Tahfizul Quran (Specialized Hifz Wing)"}</span>
              </li>
            </ul>
          </div>

          {/* Col 3: Navigation Links */}
          <div>
            <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-4 border-b border-emerald-800 pb-2">
              {lang === "bn" ? "প্রয়োজনীয় লিংক" : "Quick Navigation"}
            </h4>
            <ul className="space-y-2.5 text-xs">
              <li>
                <button
                  onClick={() => onNavigate("home")}
                  className="hover:text-amber-300 transition-colors cursor-pointer"
                >
                  {lang === "bn" ? "মূলপাতা" : "Home Page"}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate("about")}
                  className="hover:text-amber-300 transition-colors cursor-pointer"
                >
                  {lang === "bn" ? "মাদ্রাসার পরিচিতি ও ইতিহাস" : "About Our Madrasah"}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate("curriculum")}
                  className="hover:text-amber-300 transition-colors cursor-pointer"
                >
                  {lang === "bn" ? "পাঠ্যক্রম ও শ্রেণি বিভাজন" : "Curriculum & Classes"}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate("blog")}
                  className="hover:text-amber-300 transition-colors cursor-pointer"
                >
                  {lang === "bn" ? "সর্বশেষ ব্লগ ও নোটিশ" : "Articles & Announcements"}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate("contact")}
                  className="hover:text-amber-300 transition-colors cursor-pointer"
                >
                  {lang === "bn" ? "২০২৬ ভর্তি আবেদন ও যোগাযোগ" : "Admissions 2026 & Contact"}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate("admin")}
                  className="text-amber-300 hover:text-amber-200 transition-colors flex items-center gap-1 font-semibold cursor-pointer"
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                  <span>{lang === "bn" ? "এডমিন লগইন প্যানেল (/admin)" : "Admin Dashboard (/admin)"}</span>
                </button>
              </li>
            </ul>
          </div>

          {/* Col 4: Campus Office Contact Info */}
          <div>
            <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-4 border-b border-emerald-800 pb-2 flex items-center gap-1.5">
              <Building2 className="w-4 h-4 text-amber-400" />
              <span>{lang === "bn" ? settings?.campus_office_title_bn || "ক্যাম্পাস অফিস" : settings?.campus_office_title || "Campus Office"}</span>
            </h4>
            <div className="space-y-3 text-xs text-emerald-200/80">
              <div className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <span>
                  {lang === "bn" ? settings?.address_bn : settings?.address}
                </span>
              </div>
              <div className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 text-amber-400 shrink-0" />
                <span>{settings?.phone}</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Mail className="w-4 h-4 text-amber-400 shrink-0" />
                <span>{settings?.email}</span>
              </div>
              <div className="flex items-start gap-2.5">
                <Clock className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <span>
                  {lang === "bn" ? settings?.campus_office_hours_bn || "শনিবার - বৃহস্পতিবার: সকাল ৮:০০ - বিকাল ৫:০০" : settings?.campus_office_hours || "Saturday – Thursday: 8:00 AM – 5:00 PM"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-emerald-400/80">
          <p>
            &copy; {new Date().getFullYear()} {lang === "bn" ? settings?.site_name_bn : settings?.site_name}.{" "}
            {lang === "bn" ? settings?.footer_copyright_bn || "সর্বস্বত্ব সংরক্ষিত।" : settings?.footer_copyright || "All rights reserved."}
          </p>

          <div className="flex items-center gap-4">
            <span className="text-emerald-300">
              {lang === "bn" ? settings?.campus_office_landmark_bn || "তুলাতলী বাজার সংলগ্ন, দাগনভূঞা, ফেনী" : settings?.campus_office_landmark || "Tulatoli Bazar, Daganbhuiyan, Feni"}
            </span>
            <span className="text-emerald-700">·</span>
            <button
              onClick={() => onNavigate("admin")}
              className="text-amber-400 hover:text-amber-300 font-medium flex items-center gap-1 cursor-pointer"
            >
              <span>Staff Login</span>
              <ExternalLink className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};
